import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  FileCheck,
  Loader2,
  Warehouse,
  Calendar,
  Truck,
  Package,
  FileText,
  User,
  RefreshCw,
  ArrowRightLeft,
  Layers,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const LANDED_COST_KEYS = [
  'insuranceCost',
  'exwCost',
  'freightCost',
  'demurrageCost',
  'withholdingTax',
  'customsDuty',
  'customsOvertimeCost',
  'stampDutyCost',
  'customsPorterageCost',
  'commissionCost',
  'domesticFreightCost',
  'forkliftCost',
  'bankCharges',
] as const;

export function GoodsReceiptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('trading');

  const { data: receipt, isLoading, error } = trpc.tradingGoodsReceipt.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const { data: batch } = trpc.tradingGoodsReceipt.byBatchId.useQuery(
    receipt?.receiptBatchId ?? '',
    { enabled: !!receipt?.receiptBatchId }
  );

  // Fetch exchange rates for receipt date
  const receiptDate = receipt?.receiptDate ? new Date(receipt.receiptDate) : undefined;
  const poCurrency = receipt?.purchaseOrder?.currency ?? 'EUR';

  // Always fetch all rates at the receipt date — we need them for the PO
  // currency and for every distinct landed-cost currency (which may differ
  // from the PO currency, e.g. EUR freight on a USD or even TRY PO).
  const { data: ratesData } = trpc.exchangeRate.getAtDateAllRates.useQuery(
    { baseCurrency: 'TRY', date: receiptDate! },
    { enabled: !!receiptDate }
  );

  const fetchKKTCMBMutation = trpc.exchangeRate.fetchFromKKTCMB.useMutation({
    onSuccess: (data) => {
      showSuccessToast(data.message);
      utils.exchangeRate.getAtDateAllRates.invalidate();
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const refreshFxMutation = trpc.tradingGoodsReceipt.refreshFxRates.useMutation({
    onSuccess: (data) => {
      const count = data.receipts.length;
      showSuccessToast(
        t('purchasing.goodsReceipts.refreshFxSuccess', { count }) +
          // fall back to plural form when i18next doesn't resolve
          ''
      );
      utils.tradingGoodsReceipt.byId.invalidate();
      utils.tradingGoodsReceipt.byBatchId.invalidate();
      utils.tradingGoodsReceipt.list.invalidate();
      utils.stockLevel.list.invalidate();
      utils.stockLevel.getProductBatches.invalidate();
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const onRefreshFxRate = (scope: 'this' | 'batch') => {
    const count = scope === 'batch' && batch?.receipts?.length ? batch.receipts.length : 1;
    const msg =
      count > 1
        ? t('purchasing.goodsReceipts.refreshFxConfirmBatch', { count })
        : t('purchasing.goodsReceipts.refreshFxConfirmSingle');
    if (!window.confirm(msg)) return;
    refreshFxMutation.mutate({ id: id!, scope });
  };

  const utils = trpc.useUtils();

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" className="h-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('purchasing.goodsReceipts.back')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error?.message || 'Goods receipt not found'}</p>
        </div>
      </div>
    );
  }

  const po = receipt.purchaseOrder;
  const supplier = po.supplier;

  // Exchange rate: PO currency to TRY.
  // Prefer the rate that was frozen on the receipt at creation time so the
  // TRY values on this page match what's stored in stock batches, journal
  // entries, and StockLevel.averageCost. Falls back to the rate table only
  // for legacy GRNs (pre-fxRate column) and lastly to 1 for TRY POs.
  const fxRate = poCurrency === 'TRY'
    ? 1
    : receipt.fxRate ?? ratesData?.rates?.[poCurrency] ?? null;

  // Build per-currency rate rows for the FX card. Includes the PO currency
  // plus any distinct currency used on a landed-cost row. For multi-PO
  // batches we aggregate across sibling GRNs — landed costs are entered
  // once at batch level and sliced per-GRN, so a USD freight cost may live
  // entirely on one sibling while the current receipt only holds the EUR
  // items. The card needs to surface both so the user can see and refresh
  // every rate that drives a TRY projection on this batch.
  type RateRow = {
    currency: string;
    frozen: number | null;
    source: string | null;
    live: number | null;
    isPO: boolean;
  };
  const rateRows: RateRow[] = (() => {
    const byCurrency = new Map<string, RateRow>();
    if (poCurrency !== 'TRY') {
      byCurrency.set(poCurrency, {
        currency: poCurrency,
        frozen: receipt.fxRate ?? null,
        source: receipt.fxRateSource ?? null,
        live: ratesData?.rates?.[poCurrency] ?? null,
        isPO: true,
      });
    }

    type StoredDetail = { originalCurrency: string; fxRate?: number; fxRateSource?: string };
    const detailBuckets: StoredDetail[][] = [];
    if (batch?.receipts?.length) {
      for (const s of batch.receipts) {
        const sd = s.landedCostDetails as StoredDetail[] | null;
        if (sd) detailBuckets.push(sd);
      }
    } else {
      const rd = receipt.landedCostDetails as StoredDetail[] | null;
      if (rd) detailBuckets.push(rd);
    }

    for (const bucket of detailBuckets) {
      for (const d of bucket) {
        if (!d?.originalCurrency || d.originalCurrency === 'TRY') continue;
        if (byCurrency.has(d.originalCurrency)) continue;
        byCurrency.set(d.originalCurrency, {
          currency: d.originalCurrency,
          frozen: d.fxRate ?? null,
          source: d.fxRateSource ?? null,
          live: ratesData?.rates?.[d.originalCurrency] ?? null,
          isPO: false,
        });
      }
    }
    return Array.from(byCurrency.values());
  })();
  const anyDrift = rateRows.some(
    (r) => r.frozen != null && r.live != null && Math.abs(r.live - r.frozen) > 0.0001
  );

  const landedComponents = LANDED_COST_KEYS
    .map((key) => ({
      key,
      value: receipt[key] as number | null,
    }))
    .filter((c) => c.value != null && c.value > 0);

  const totalLandedComponents = landedComponents.reduce((sum, c) => sum + (c.value ?? 0), 0);

  // KDV data from the JSON field
  const kdvData = (receipt.landedCostKdv as Array<{ key: string; kdvRate: number; kdvAmount: number; isKdvOnly: boolean }>) ?? [];
  const kdvByKey = new Map(kdvData.map((k) => [k.key, k]));
  const totalKdv = kdvData.reduce((sum, k) => sum + k.kdvAmount, 0);

  // Per-row audit trail (post-2026-04 writes). Legacy records without this
  // field fall back to the flat-column display below.
  const landedCostDetails = (receipt.landedCostDetails as Array<{
    key: string;
    originalCurrency: string;
    originalAmount: number;
    fxRate: number;
    fxRateSource: string;
    amountTRY: number;
    kdvRate: number;
    kdvAmountOriginal: number;
    kdvAmountTRY: number;
    isKdvOnly: boolean;
    method: string;
    allocationPct: number;
    sellerId: string | null;
  }> | null) ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" className="h-8" onClick={() => navigate('/trading/purchasing/receipts')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('purchasing.goodsReceipts.back')}
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-muted-foreground" />
              <h1 className="text-xl font-semibold">{receipt.receiptNumber}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('purchasing.goodsReceipts.goodsReceiptFor')}{' '}
              <Link
                to={`/trading/purchasing/${receipt.purchaseOrderId}`}
                className="text-primary hover:underline font-mono"
              >
                {po.orderNumber}
              </Link>
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          className="h-8"
          onClick={() => navigate(`/trading/purchasing/receipts/${receipt.id}/edit-landed-costs`)}
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          {t('purchasing.goodsReceipts.editLandedCosts')}
        </Button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-border p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Truck className="h-3.5 w-3.5" />
            {t('purchasing.goodsReceipts.supplier')}
          </div>
          <p className="text-sm font-medium">{supplier.companyName}</p>
        </div>
        <div className="border border-border p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Warehouse className="h-3.5 w-3.5" />
            {t('purchasing.goodsReceipts.warehouse')}
          </div>
          <p className="text-sm font-medium">{receipt.warehouse.name}</p>
        </div>
        <div className="border border-border p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            {t('purchasing.goodsReceipts.receiptDateLabel')}
          </div>
          <p className="text-sm font-medium">{formatDate(receipt.receiptDate)}</p>
        </div>
        <div className="border border-border p-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            {t('purchasing.goodsReceipts.createdBy')}
          </div>
          <p className="text-sm font-medium">
            {receipt.createdBy.firstName} {receipt.createdBy.lastName}
          </p>
          <p className="text-xs text-muted-foreground">{formatDate(receipt.createdAt)}</p>
        </div>
      </div>

      {/* Multi-PO Batch Panel — shows sibling GRNs and pre-split landed cost totals */}
      {batch && batch.receipts.length > 1 && (
        <div className="border border-border">
          <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">
              {t('purchasing.goodsReceipts.batchTitle')} ({batch.receipts.length})
            </h2>
          </div>
          <div className="px-4 py-3 text-xs text-muted-foreground border-b border-border">
            {t('purchasing.goodsReceipts.batchDescription', { count: batch.receipts.length })}
          </div>

          {/* Sibling receipts table */}
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="border-b border-border">
                <th className="px-4 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.receiptNumber')}</th>
                <th className="px-4 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.purchaseOrder')}</th>
                <th className="px-4 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.warehouse')}</th>
                <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.items')}</th>
                <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.itemsSubtotal')}</th>
                <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.landedCostsLabel')} (TRY)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {batch.receipts.map((sibling) => {
                const siblingLandedTotal = Object.values(sibling.landedCosts).reduce((s, v) => s + (v ?? 0), 0);
                const isCurrent = sibling.id === receipt.id;
                return (
                  <tr key={sibling.id} className={isCurrent ? 'bg-primary/5' : 'hover:bg-muted/20'}>
                    <td className="px-4 py-1.5">
                      {isCurrent ? (
                        <span className="font-mono text-sm font-medium">
                          {sibling.receiptNumber}
                          <span className="ml-2 bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                            {t('purchasing.goodsReceipts.currentReceipt')}
                          </span>
                        </span>
                      ) : (
                        <Link
                          to={`/trading/purchasing/receipts/${sibling.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {sibling.receiptNumber}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-1.5">
                      <Link
                        to={`/trading/purchasing/${sibling.purchaseOrderId}`}
                        className="font-mono text-xs text-muted-foreground hover:text-primary"
                      >
                        {sibling.purchaseOrderNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-1.5 text-xs">{sibling.warehouseName}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs">{sibling.itemCount}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs">
                      {formatCurrency(sibling.itemsSubtotal, sibling.currency)}
                    </td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs">
                      {siblingLandedTotal > 0 ? formatCurrency(siblingLandedTotal, 'TRY') : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pivoted landed-cost breakdown: rows = cost types, columns = sibling GRNs + original total */}
          {(() => {
            const activeKeys = LANDED_COST_KEYS.filter(
              (key) => (batch.aggregatedLandedCosts[key] ?? 0) > 0
            );
            if (activeKeys.length === 0) return null;

            const grnTotals = batch.receipts.map((s) =>
              Object.values(s.landedCosts).reduce((sum, v) => sum + (v ?? 0), 0)
            );
            const batchGrandTotal = Object.values(batch.aggregatedLandedCosts).reduce(
              (sum, v) => sum + v,
              0
            );

            return (
              <div className="border-t border-border overflow-x-auto">
                <div className="px-4 py-2 bg-muted/20 text-xs font-medium">
                  {t('purchasing.goodsReceipts.landedCostBreakdown')} (TRY)
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/10">
                    <tr className="border-b border-border">
                      <th className="px-4 py-1.5 text-left font-medium text-xs sticky left-0 bg-muted/10">
                        {t('purchasing.goodsReceipts.costType')}
                      </th>
                      <th className="px-3 py-1.5 text-right font-medium text-xs border-l border-border">
                        {t('purchasing.goodsReceipts.batchOriginalTotal')}
                      </th>
                      {batch.receipts.map((sibling) => {
                        const isCurrent = sibling.id === receipt.id;
                        return (
                          <th
                            key={sibling.id}
                            className={`px-3 py-1.5 text-right font-medium text-xs border-l border-border ${
                              isCurrent ? 'bg-primary/10 text-primary' : ''
                            }`}
                          >
                            {isCurrent ? (
                              <span className="font-mono">{sibling.receiptNumber}</span>
                            ) : (
                              <Link
                                to={`/trading/purchasing/receipts/${sibling.id}`}
                                className="font-mono hover:underline"
                              >
                                {sibling.receiptNumber}
                              </Link>
                            )}
                            <div className="font-normal font-mono text-[10px] text-muted-foreground mt-0.5">
                              {sibling.purchaseOrderNumber}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activeKeys.map((key) => {
                      const total = batch.aggregatedLandedCosts[key] ?? 0;
                      return (
                        <tr key={key} className="hover:bg-muted/20">
                          <td className="px-4 py-1.5 sticky left-0 bg-background">
                            {t(`purchasing.goodsReceipts.lc_${key}`)}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs border-l border-border">
                            {formatCurrency(total, 'TRY')}
                          </td>
                          {batch.receipts.map((sibling) => {
                            const allocated = sibling.landedCosts[key] ?? 0;
                            const share = total > 0 ? (allocated / total) * 100 : 0;
                            const isCurrent = sibling.id === receipt.id;
                            return (
                              <td
                                key={sibling.id}
                                className={`px-3 py-1.5 text-right font-mono text-xs border-l border-border ${
                                  isCurrent ? 'bg-primary/5' : ''
                                }`}
                              >
                                {allocated > 0 ? (
                                  <>
                                    <div className={isCurrent ? 'text-orange-700 font-medium' : 'text-orange-700'}>
                                      {formatCurrency(allocated, 'TRY')}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                      {share.toFixed(2)}%
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted-foreground/40">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/30 border-t border-border">
                    <tr>
                      <td className="px-4 py-2 font-medium sticky left-0 bg-muted/30">
                        {t('purchasing.goodsReceipts.totalLandedCostsLabel')}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-xs border-l border-border">
                        {formatCurrency(batchGrandTotal, 'TRY')}
                      </td>
                      {batch.receipts.map((sibling, idx) => {
                        const isCurrent = sibling.id === receipt.id;
                        const grnTotal = grnTotals[idx] ?? 0;
                        const grnShare = batchGrandTotal > 0 ? (grnTotal / batchGrandTotal) * 100 : 0;
                        return (
                          <td
                            key={sibling.id}
                            className={`px-3 py-2 text-right font-mono text-xs border-l border-border ${
                              isCurrent ? 'bg-primary/10' : ''
                            }`}
                          >
                            <div className={`font-bold ${isCurrent ? 'text-orange-700' : 'text-orange-700'}`}>
                              {formatCurrency(grnTotal, 'TRY')}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-normal">
                              {grnShare.toFixed(2)}%
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Exchange Rate Card — shows the frozen rate for every currency used
          on the receipt (PO currency + each distinct landed-cost currency). */}
      {rateRows.length > 0 && (
        <div className="border border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-medium">
                {t('purchasing.goodsReceipts.exchangeRate')} ({rateRows.length === 1 ? `${rateRows[0]!.currency}/TRY` : 'TRY'})
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {anyDrift && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                  disabled={refreshFxMutation.isPending}
                  onClick={() => onRefreshFxRate(batch?.receipts && batch.receipts.length > 1 ? 'batch' : 'this')}
                >
                  {refreshFxMutation.isPending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-3 w-3" />
                  )}
                  {batch?.receipts && batch.receipts.length > 1
                    ? t('purchasing.goodsReceipts.refreshFxForBatch', {
                        count: batch.receipts.length,
                      })
                    : t('purchasing.goodsReceipts.refreshFxForThis')}
                </Button>
              )}
              <Button
                variant="outline"
                className="h-8"
                onClick={() => fetchKKTCMBMutation.mutate({ date: receiptDate! })}
                disabled={fetchKKTCMBMutation.isPending || !receiptDate}
              >
                {fetchKKTCMBMutation.isPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                )}
                {t('purchasing.goodsReceipts.fetchKKTCMBRates')}
              </Button>
            </div>
          </div>

          <table className="w-full text-sm mt-3">
            <thead className="bg-muted/20">
              <tr className="border-b border-border">
                <th className="px-3 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.rateTableCurrency')}</th>
                <th className="px-3 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.rateTableRole')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.rateTableFrozen')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.rateTableLive')}</th>
                <th className="px-3 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.rateTableSource')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rateRows.map((row) => {
                const drift =
                  row.frozen != null && row.live != null && Math.abs(row.live - row.frozen) > 0.0001;
                return (
                  <tr key={row.currency} className={drift ? 'bg-amber-50/50' : ''}>
                    <td className="px-3 py-1.5 font-mono font-medium">{row.currency}/TRY</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">
                      {row.isPO
                        ? t('purchasing.goodsReceipts.rolePOCurrency')
                        : t('purchasing.goodsReceipts.roleLandedCost')}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {row.frozen != null ? (
                        <span className={drift ? 'text-amber-700 font-medium' : ''}>
                          {row.frozen.toFixed(4)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          {t('purchasing.goodsReceipts.rateNotFrozen')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {row.live != null ? (
                        <span className={drift ? 'text-amber-700' : 'text-muted-foreground'}>
                          {row.live.toFixed(4)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-[11px] uppercase text-muted-foreground">
                      {row.source ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {anyDrift && (
            <p className="mt-2 text-xs text-amber-700">
              {t('purchasing.goodsReceipts.rateDriftSummary')}
            </p>
          )}

          {fxRate != null && receipt.itemsSubtotal != null && poCurrency !== 'TRY' && (
            <div className="mt-2 text-xs text-muted-foreground">
              {t('purchasing.goodsReceipts.itemsSubtotalTRY')}:{' '}
              <span className="ml-1 font-mono font-medium text-foreground">
                {formatCurrency(receipt.itemsSubtotal * fxRate, 'TRY')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Items Table */}
      <div className="border border-border">
        <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">
            {t('purchasing.goodsReceipts.receivedItems')} ({receipt.items.length})
          </h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr className="border-b border-border">
              <th className="px-4 py-2 text-left font-medium">{t('purchasing.goodsReceipts.product')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.qty')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.poQty')}</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.purchasePrice')} ({poCurrency})</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.itemValue')} ({poCurrency})</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.landedPerUnit')} (TRY)</th>
              <th className="px-4 py-2 text-right font-medium">{t('purchasing.goodsReceipts.totalCostPerUnit')} (TRY)</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {receipt.items.map((item) => {
              const itemValue = item.quantity * item.unitCost;
              const landed = item.landedCostAllocation ?? 0;
              // Total cost per unit in TRY: purchase converted to TRY + landed (already TRY)
              const purchaseInTRY = fxRate ? item.unitCost * fxRate : null;
              const totalUnitCostTRY = purchaseInTRY != null ? purchaseInTRY + landed : null;
              return (
                <tr key={item.id} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <Link
                      to={`/trading/products/${item.productId}`}
                      className="font-medium hover:text-primary hover:underline"
                    >
                      {item.productName}
                    </Link>
                    <div className="font-mono text-xs text-muted-foreground">{item.productCode}</div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{item.quantity}</td>
                  <td className="px-4 py-2 text-right font-mono text-muted-foreground">{item.poQuantity}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {formatCurrency(item.unitCost, poCurrency)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                    {formatCurrency(itemValue, poCurrency)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {landed > 0 ? (
                      <span className="text-orange-700">
                        +{formatCurrency(landed, 'TRY')}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-medium">
                    {totalUnitCostTRY != null ? (
                      formatCurrency(totalUnitCostTRY, 'TRY')
                    ) : (
                      <span className="text-muted-foreground text-xs">{t('purchasing.goodsReceipts.needFxRate')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-muted/30 border-t border-border">
            <tr>
              <td className="px-4 py-2 font-medium" colSpan={4}>{t('purchasing.goodsReceipts.totals')}</td>
              <td className="px-4 py-2 text-right font-mono font-medium">
                {formatCurrency(receipt.itemsSubtotal ?? 0, poCurrency)}
                {fxRate && (
                  <div className="text-xs text-muted-foreground font-normal">
                    = {formatCurrency((receipt.itemsSubtotal ?? 0) * fxRate, 'TRY')}
                  </div>
                )}
              </td>
              <td className="px-4 py-2 text-right font-mono font-medium text-orange-700">
                {totalLandedComponents > 0
                  ? `+${formatCurrency(totalLandedComponents, 'TRY')}`
                  : '-'}
              </td>
              <td className="px-4 py-2 text-right font-mono font-bold">
                {fxRate ? (
                  formatCurrency(
                    (receipt.itemsSubtotal ?? 0) * fxRate + totalLandedComponents,
                    'TRY'
                  )
                ) : '-'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Landed Costs Breakdown */}
      {(landedComponents.length > 0 || kdvData.length > 0) && (
        <div className="border border-border">
          <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center gap-2">
            <Truck className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">{t('purchasing.goodsReceipts.landedCostBreakdown')}</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="border-b border-border">
                <th className="px-4 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.costType')}</th>
                {landedCostDetails && (
                  <>
                    <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.detailOriginalHeader')}</th>
                    <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.detailFxHeader')}</th>
                  </>
                )}
                <th className="px-4 py-1.5 text-right font-medium text-xs">{t('purchasing.goodsReceipts.amount')} (TRY)</th>
                {kdvData.length > 0 && (
                  <th className="px-4 py-1.5 text-right font-medium text-xs">KDV</th>
                )}
                {landedCostDetails && (
                  <th className="px-4 py-1.5 text-left font-medium text-xs">{t('purchasing.goodsReceipts.detailSellerHeader')}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {landedComponents.map((cost) => {
                const kdv = kdvByKey.get(cost.key);
                const detail = landedCostDetails?.find((d) => d.key === cost.key && !d.isKdvOnly);
                return (
                  <tr key={cost.key} className="hover:bg-muted/20">
                    <td className="px-4 py-1.5">{t(`purchasing.goodsReceipts.lc_${cost.key}`)}</td>
                    {landedCostDetails && (
                      <>
                        <td className="px-4 py-1.5 text-right font-mono text-xs">
                          {detail
                            ? formatCurrency(detail.originalAmount, detail.originalCurrency)
                            : '-'}
                        </td>
                        <td className="px-4 py-1.5 text-right font-mono text-[11px] text-muted-foreground">
                          {detail
                            ? detail.originalCurrency === 'TRY'
                              ? '1.0000'
                              : detail.fxRate.toFixed(4)
                            : '-'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-1.5 text-right font-mono">{formatCurrency(cost.value!, 'TRY')}</td>
                    {kdvData.length > 0 && (
                      <td className="px-4 py-1.5 text-right font-mono text-muted-foreground">
                        {kdv ? `%${kdv.kdvRate} = ${formatCurrency(kdv.kdvAmount, 'TRY')}` : '-'}
                      </td>
                    )}
                    {landedCostDetails && (
                      <td className="px-4 py-1.5 text-left text-xs text-muted-foreground truncate max-w-[140px]">
                        {detail?.sellerId ? detail.sellerId.slice(0, 8) + '…' : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
              {/* KDV-only rows (not in landedComponents since base = 0) */}
              {kdvData.filter((k) => k.isKdvOnly).map((kdv) => {
                const detail = landedCostDetails?.find((d) => d.key === kdv.key && d.isKdvOnly);
                return (
                  <tr key={kdv.key} className="hover:bg-muted/20">
                    <td className="px-4 py-1.5 text-muted-foreground">
                      {t(`purchasing.goodsReceipts.lc_${kdv.key}`)}
                      <span className="text-xs ml-1">(KDV)</span>
                    </td>
                    {landedCostDetails && (
                      <>
                        <td className="px-4 py-1.5 text-right font-mono text-xs">
                          {detail
                            ? formatCurrency(detail.kdvAmountOriginal, detail.originalCurrency)
                            : '-'}
                        </td>
                        <td className="px-4 py-1.5 text-right font-mono text-[11px] text-muted-foreground">
                          {detail
                            ? detail.originalCurrency === 'TRY'
                              ? '1.0000'
                              : detail.fxRate.toFixed(4)
                            : '-'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-1.5 text-right font-mono text-muted-foreground">-</td>
                    <td className="px-4 py-1.5 text-right font-mono text-muted-foreground">
                      %{kdv.kdvRate} = {formatCurrency(kdv.kdvAmount, 'TRY')}
                    </td>
                    {landedCostDetails && (
                      <td className="px-4 py-1.5 text-left text-xs text-muted-foreground truncate max-w-[140px]">
                        {detail?.sellerId ? detail.sellerId.slice(0, 8) + '…' : '—'}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t border-border">
              <tr className="bg-muted/30">
                <td className="px-4 py-2 font-medium">{t('purchasing.goodsReceipts.totalLandedCostsLabel')}</td>
                {landedCostDetails && <td colSpan={2} />}
                <td className="px-4 py-2 text-right font-mono font-bold">{formatCurrency(totalLandedComponents, 'TRY')}</td>
                {kdvData.length > 0 && (
                  <td className="px-4 py-2 text-right font-mono font-bold text-blue-700">
                    {formatCurrency(totalKdv, 'TRY')}
                  </td>
                )}
                {landedCostDetails && <td />}
              </tr>
              {fxRate && receipt.itemsSubtotal && (
                <tr className="bg-muted/50">
                  <td className="px-4 py-2 font-medium">
                    {t('purchasing.goodsReceipts.grandTotalLabel', { currency: poCurrency })}
                  </td>
                  <td
                    className="px-4 py-2 text-right font-mono font-bold"
                    colSpan={
                      (landedCostDetails ? 2 : 0) + 1 + (kdvData.length > 0 ? 1 : 0) + (landedCostDetails ? 1 : 0)
                    }
                  >
                    {formatCurrency(
                      receipt.itemsSubtotal * fxRate + totalLandedComponents,
                      'TRY'
                    )}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}

      {/* Notes & Documents */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {receipt.notes && (
          <div className="border border-border p-4 space-y-2">
            <h2 className="text-sm font-medium">{t('purchasing.goodsReceipts.notesLabel')}</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{receipt.notes}</p>
          </div>
        )}

        {receipt.documents.length > 0 && (
          <div className="border border-border p-4 space-y-2">
            <h2 className="text-sm font-medium flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {t('purchasing.goodsReceipts.documents')} ({receipt.documents.length})
            </h2>
            <div className="space-y-1.5">
              {receipt.documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{doc.fileName}</span>
                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                    {doc.costType && (
                      <span className="bg-muted px-1.5 py-0.5 mr-1">
                        {t(`purchasing.goodsReceipts.lc_${doc.costType}`)}
                      </span>
                    )}
                    {(doc.fileSize / 1024).toFixed(0)} KB
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {receipt.supplierInvoice && (
          <div className="border border-border p-4 space-y-2">
            <h2 className="text-sm font-medium">{t('purchasing.goodsReceipts.supplierInvoiceLabel')}</h2>
            <p className="text-sm font-mono">{receipt.supplierInvoice}</p>
          </div>
        )}
      </div>

      {/* Journal Entry Reference */}
      {receipt.journalEntry && (
        <div className="border border-border p-4">
          <h2 className="text-sm font-medium mb-2">{t('purchasing.goodsReceipts.journalEntryLabel')}</h2>
          <Link
            to={`/trading/accounting/journal-entries/${(receipt.journalEntry as { id: string }).id}`}
            className="text-sm font-mono text-primary hover:underline"
          >
            {(receipt.journalEntry as { entryNumber: string }).entryNumber}
          </Link>
        </div>
      )}
    </div>
  );
}
